// Frontend mirror of the RBAC context layer (docs/rbac.md).
//
// Capabilities are NOT mirrored here — the backend computes them per binding
// and returns them on /login and /auth/me (user.capabilities). This module
// only owns presentation concerns: team labels, escalation targets, and which
// pages a binding can see. UI gating is UX only; the backend enforces.
import type { LoginResponseUser } from '../types/api';

// Team ids are the original demo names; labels are what people see.
// wayne = Receiving (fulfillment services, material intake),
// cary = Proxy/ICT (the customer-facing team who owns the job orders).
export const TEAMS = [
  { id: 'wayne', label: 'Receiving' },
  { id: 'cary', label: 'Proxy/ICT' },
] as const;

export type TeamId = (typeof TEAMS)[number]['id'];

export const teamLabel = (id: string | null | undefined): string =>
  TEAMS.find(t => t.id === id)?.label ?? (id || '');

export const roleLabel = (role: string): string =>
  role.charAt(0).toUpperCase() + role.slice(1);

// Material-intake workflow: exceptions escalate to the team that owns the
// job orders (the workflow's escalation_target stage assignment).
export const ESCALATION_TARGETS = TEAMS.filter(t => t.id === 'cary');

export const can = (user: LoginResponseUser | null, capability: string): boolean =>
  !!user?.capabilities?.includes(capability);

// ── Binding vocabulary (mirrors backend rbac.ROLES_BY_SCOPE) ────────────────
// Used by the user-management form to offer only valid (scope, role) combos.
export const ORG_DEFAULT = 'default';

export type Scope = 'platform' | 'org' | 'team';

export const SCOPES: Array<{ id: Scope; label: string }> = [
  { id: 'platform', label: 'Nexus' },
  { id: 'org', label: 'Organization' },
  { id: 'team', label: 'Team' },
];

export const ROLES_BY_SCOPE: Record<Scope, string[]> = {
  platform: ['superadmin', 'operator'],
  org: ['admin', 'developer', 'guest'],
  team: ['manager', 'operator'],
};

// Resolve the org_id / team_id a (scope, team) selection implies.
export function bindingIds(scope: Scope, teamId: string): { org_id: string | null; team_id: string | null } {
  if (scope === 'platform') return { org_id: null, team_id: null };
  if (scope === 'org') return { org_id: ORG_DEFAULT, team_id: null };
  return { org_id: ORG_DEFAULT, team_id: teamId };
}

export function scopeOf(user: { org_id?: string | null; team_id?: string | null }): Scope {
  if (!user.org_id) return 'platform';
  if (!user.team_id) return 'org';
  return 'team';
}

// Pages a guest's allowlist can grant (the work-surface pages, not admin).
export const GUEST_PAGE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'ace', label: 'Ace' },
  { id: 'receiving-log', label: 'Receiving Log' },
  { id: 'production-log', label: 'Production Log' },
  { id: 'intake-queue', label: 'Intake Queue' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'all-receipts', label: 'All Receipts' },
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'follow-ups', label: 'Follow Ups' },
  { id: 'overs', label: 'Overs' },
  { id: 'escalations', label: 'Escalations' },
  { id: 'jobs', label: 'All Jobs' },
  { id: 'email-intake', label: 'Email Intake' },
  { id: 'advisor-support', label: 'Advisor Support' },
  { id: 'history', label: 'History' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'extraction-accuracy', label: 'Extraction Accuracy' },
];

// ── Page visibility ──────────────────────────────────────────────────────────

// Team bindings (Wayne, Cary) see every page — no per-team page restriction.
// `teamOverride` (AppShellContext's demoTeam) is accepted for call-site
// compatibility with the demo "viewing as" toggle, but no longer narrows
// page visibility; only Guest allowlists and capability checks do.
export function pageVisible(
  user: LoginResponseUser | null,
  pageId: string,
  teamOverride?: string | null,
): boolean {
  if (!user) return false;
  if (pageId === 'profile') return true;
  if (pageId === 'admin-users') return can(user, 'user.manage');
  if (pageId === 'admin-feedback') return can(user, 'feedback.manage');
  // Guests are confined to their per-user allowlist (configurable by Admin).
  if (user.role === 'guest') return (user.page_access ?? []).includes(pageId);
  if (pageId === 'workflows') return can(user, 'workflow.view');
  return true;
}

// Route paths by page id, in landing-preference order.
export const PAGE_PATHS: Array<{ id: string; path: string }> = [
  { id: 'ace', path: '/ace' },
  { id: 'receiving-log', path: '/receiving-log' },
  { id: 'production-log', path: '/production-log' },
  { id: 'escalations', path: '/escalations' },
  { id: 'jobs', path: '/jobs' },
  { id: 'inventory', path: '/inventory' },
  { id: 'all-receipts', path: '/all-receipts' },
  { id: 'intake-queue', path: '/intake-queue' },
  { id: 'exceptions', path: '/exceptions' },
  { id: 'follow-ups', path: '/follow-ups' },
  { id: 'overs', path: '/overs' },
  { id: 'email-intake', path: '/email-intake' },
  { id: 'advisor-support', path: '/advisor-support' },
  { id: 'history', path: '/history' },
  { id: 'workflows', path: '/workflows' },
  { id: 'admin-users', path: '/admin/users' },
  { id: 'admin-feedback', path: '/admin/feedback' },
  { id: 'extraction-accuracy', path: '/data/extraction-accuracy' },
  { id: 'profile', path: '/profile' },
];

// Where to land a user after login / on a forbidden page (profile is always
// visible, so this always resolves). Pass teamOverride to route to a demo
// persona's first page when the "viewing as" toggle changes.
export const defaultPathFor = (user: LoginResponseUser | null, teamOverride?: string | null): string =>
  PAGE_PATHS.find(p => pageVisible(user, p.id, teamOverride))?.path ?? '/profile';
